import { usePostHog } from 'posthog-js/react';
import { FC, useState } from 'react';

import { ImagesApp } from '../../constants/images';
import { useBreadCrumbsContext } from '../../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../../hooks/contexts/cart.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import useGuardian from '../../hooks/useGuardian';
import formatNumericValue from '../../utils/formatNumericValue';
import shouldDisableButton from '../../utils/shouldDisableButton';
import { BigButton } from '../Buttons';
import SelectedTickets, { TicketCart } from '../EventDetails/Summary/SelectedTickets';
import * as Styles from './summary-mobile.styles';

const SummaryMobile: FC = () => {
  const posthog = usePostHog();
  const [openDetails, setOpenDetails] = useState(false);

  const { translate } = useI18nContext();
  const { currencyFormat, loading, event } = useEventContext();
  const { summaryData, getTotalTicketsInCart, namingData, cart, combosInCart, cartLoading } = useCartContext();
  const { isNamingPage } = useBreadCrumbsContext();
  const { navigateToNextRoute } = useGuardian();

  const sumValues = () => {
    const totalWithoutCombo = summaryData.reduce((acc, ticket) => acc + ticket.accumulatedValueInCart, 0);

    const totalJustCombo = combosInCart.reduce((acc, combo) => {
      const totalPerCombo = combo.comboDates.reduce((insideAcc, date) => {
        const value = date.groups[0].tickets[0].price;
        return insideAcc + (value - (value * date.discount) / 100);
      }, 0) * combo.quantity;

      return acc + totalPerCombo;
    }, 0);

    return totalWithoutCombo + totalJustCombo;
  };

  const hasMinimumTickets = cart.map((item) =>
    item.type.map((ticket) => ticket.quantityInCart < ticket.restrictions.minimum)
  );
  const findMinimumTickets = hasMinimumTickets.findIndex((item, i) => item[i] === true);
  const transferIsRequired = event?.attributes.ticketTransferRequired.value;
  const hasMandatoryFaceRecognition = namingData.some((curData) => curData.transferMandatoryFaceRecognition);
  const hasOptionalFaceRecognitionAndNoTransferable = namingData.some((curData) => (
    curData.faceRecognition && !curData.isTransferableGuestType
  ));

  const isTheNamingPageAndIsOk = namingData.reduce((acc, item) => {
    return item.holder ? acc : acc + 1;
  }, 0) > 0 && isNamingPage;

  const verifyDisableBtnRule = (
    transferIsRequired ||
    hasMandatoryFaceRecognition ||
    hasOptionalFaceRecognitionAndNoTransferable
  );

  const disableBtnInNamingPage = verifyDisableBtnRule ? isTheNamingPageAndIsOk : false;

  const handleDetails = () => {
    setOpenDetails(!openDetails);
  };

  const handleNavigate = () => {
    posthog?.capture('purchase:ticket_selection_continue_click');
    navigateToNextRoute();
  };

  const isDisabled = shouldDisableButton({
    isNamingPage,
    disableBtnInNamingPage,
    findMinimumTickets,
    cart,
    combosInCart,
    loading,
    cartLoading
  });

  return (
    <Styles.Container
      $isOpen={openDetails}
      id="summary-mb"
    >
      <Styles.RowFullWidth $isCopyright={false}>
        <Styles.Column>
          <Styles.TextInfo $weight={500} size={12}>
            {getTotalTicketsInCart()}
            {' '}
            {translate(['STEPS', 'TXT4'])}
          </Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={16}>
            {formatNumericValue({
              value : sumValues(),
              discount : 0,
              currencyFormat
            })}
            {' '}
            {summaryData.some(ticket => ticket.price > 0 && ticket.tax > 0) && (
              <Styles.TextInfo $weight={500} size={12}>{translate(['STATUS_BAR', 'TEXT_5'])}</Styles.TextInfo>
            )}
          </Styles.TextInfo>
        </Styles.Column>
        <Styles.Column>
          <button onClick={handleDetails}>
            <img
              src={openDetails ? ImagesApp.svg.arrowDown : ImagesApp.svg.arrowUp}
              alt="seta pra cima"
            />
          </button>
        </Styles.Column>
      </Styles.RowFullWidth>
      {openDetails && (
        <Styles.Detailing $isVisible={openDetails} id="summary-detailing">
          <SelectedTickets
            tickets={summaryData as unknown as TicketCart[]}
            combosInCart={combosInCart}
          />
        </Styles.Detailing>
      )}
      <Styles.RowFullWidth $isCopyright={false}>
        <BigButton
          onClick={handleNavigate}
          disabled={isDisabled}
        >
          {translate(['STEPS', 'TXT15'])}
        </BigButton>
      </Styles.RowFullWidth>
    </Styles.Container>
  );
};

export default SummaryMobile;
